import React, { useRef, useEffect, useState } from 'react';
import { Box, Chip, Grid, ListItem, List, Typography, Tooltip, useTheme } from '@mui/material';
import { ArrowUpwardRounded, ArrowDownwardRounded, InfoRounded } from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { roundHalf } from '../../../lib/helpers/helpers';

const useStyles = makeStyles((theme) => ({
  list: {
    ...theme.globalClasses.list,
    color: theme.palette.text.primary,
    padding: 0,
    margin: 0,
  },
  frame: {
    ...theme.globalClasses.frame,
    width: '100%',
    padding: '16px',
  },
  listItemContainer: {
    display: 'flex',
    width: '100%',
    gap: '8px',
  },
  flexRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  flexColumn: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  labelContainer: {
    flex: '1',
    marginRight: '8px',
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

/**
 * The Patient Nutrient Summary Box displays the data:
 * - heading (string)
 * - tip (string) for tooltip
 * - data (dynamic data)
 * - isIncrease (boolean) for increase or decrease heading, color and tooltip
 * - maxWidth (number) Maximum width of the box in pixels, defaults to 336
 *
 * If there is no data, the box will not be displayed.
 *
 * @param {object} data
 * @returns
 */
const PatientNutrientSummaryBox = ({ heading, tip, data, isIncrease = false, maxWidth = '336px' }) => {
  const classes = useStyles();
  const theme = useTheme();
  const containerRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setIsNarrow(width <= 300);
      }
    };

    // Initial check
    checkWidth();

    // Add resize listener
    window.addEventListener('resize', checkWidth);

    // Cleanup
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  if (!data || data.length === 0) {
    return <></>;
  }

  const getDataHeading = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isIncrease ? (
            <ArrowUpwardRounded sx={{ color: theme.palette.successColors.light }} />
          ) : (
            <ArrowDownwardRounded sx={{ color: theme.palette.errorColors.main }} />
          )}
          <Typography variant={'h3'}>{heading}</Typography>
        </Box>
        <Tooltip
          title={tip}
          placement="top"
        >
          <InfoRounded sx={{ color: theme.palette.toolTip.active }} />
        </Tooltip>
      </Box>
    );
  };

  const getChipData = (data) => {
    if (!data) {
      return null;
    }

    const diff = isIncrease ? roundHalf(data) : roundHalf(Math.abs(data));
    const prefix = isIncrease ? '+' : '-';
    const labelData = `${prefix} ${diff} servings`;
    return (
      <Chip
        sx={{
          color: isIncrease ? theme.palette.success : theme.palette.error,
          backgroundColor: isIncrease ? theme.globalClasses.chipSuccess : theme.globalClasses.chipError,
        }}
        label={labelData}
      />
    );
  };

  // Determine border color based on isIncrease state; slightly different than chip border
  const border = isIncrease ? 'rgba(46, 125, 50, 0.50)' : 'rgba(211, 47, 47, 0.50)';

  return (
    <Box sx={{ width: '100%', maxWidth: `${maxWidth}` }}>
      <Box
        className={classes.frame}
        ref={containerRef}
        sx={{
          border: `1px solid ${border} !important`,
          maxWidth: '100%',
        }}
      >
        {getDataHeading()}
        <List
          dense={true}
          className={classes.list}
          sx={{ width: '100%' }}
        >
          {data.map((item, index) => (
            <ListItem
              key={index}
              disableGutters
            >
              <Box className={`${classes.listItemContainer} ${isNarrow ? classes.flexColumn : classes.flexRow}`}>
                <Box className={classes.labelContainer}>
                  <Typography>{item.label}</Typography>
                </Box>
                <Box className={classes.chipContainer}>{getChipData(item?.change?.difference)}</Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default PatientNutrientSummaryBox;
